import type { Wallet } from './wallet';
import { spendCoins, spendMaterial, grantCoins, grantMaterial } from './wallet';
import type { Health } from '../combat/damage';

/**
 * Wave L-3 (PLAN-CITY2 §6): extends S11's one-recipe craft bench
 * (`ARMOR_COST_SCRAP`/`ARMOR_COST_COINS`/`ARMOR_BONUS_HP`, still defined and
 * unchanged in main.ts — this file adds ON TOP of it, doesn't replace it) to
 * a real 3-step bench: Scrap Armor (unchanged, S11) -> Riot Plating (NEW,
 * consumes the new `part` material) -> Salvage Exchange (NEW, repeatable
 * forever once both HP tiers are done — a utility sink for excess parts,
 * not a stat upgrade). One interactable, one `'craft'` action, sequential —
 * matches CRAFT_BENCH's existing single-spot UX rather than inventing a
 * multi-recipe selection screen this session doesn't have room to also
 * design well.
 */
export const RIOT_PLATING_COST_PARTS = 6;
export const RIOT_PLATING_COST_COINS = 60;
export const RIOT_PLATING_BONUS_HP = 20;

export const SALVAGE_EXCHANGE_COST_PARTS = 5;
export const SALVAGE_EXCHANGE_GAIN_COINS = 20;
export const SALVAGE_EXCHANGE_GAIN_SCRAP = 2;

export interface CraftProgress {
  armorCrafted: boolean;
  platingCrafted: boolean;
}

export interface CraftAttemptResult {
  /** True if wallet/health/progress were actually mutated — the caller (main.ts) only snapshots the save on a real change, same discipline every other one-time-reward interactable already follows. */
  changed: boolean;
  toast: string;
}

/**
 * Called from main.ts's `'craft'` interact case, AFTER the existing Scrap
 * Armor branch has already run and found `save.progress.armorCrafted` true
 * (Scrap Armor itself is untouched — still main.ts's own inline logic, S11's
 * original code). Pure function: mutates `wallet`/`playerHealth`/`progress`
 * directly (matching this codebase's existing `spendMaterial`/`grantCoins`
 * choke-point convention — nothing here writes a field those functions
 * don't already own) and returns a toast string for the caller to show.
 */
export function attemptNextCraftTier(progress: CraftProgress, wallet: Wallet, playerHealth: Health): CraftAttemptResult {
  if (!progress.platingCrafted) {
    if (wallet.materials.part < RIOT_PLATING_COST_PARTS || wallet.coins < RIOT_PLATING_COST_COINS) {
      return { changed: false, toast: `🛡️ RIOT PLATING — need ${RIOT_PLATING_COST_PARTS} parts + ${RIOT_PLATING_COST_COINS} coins` };
    }
    spendMaterial(wallet, 'part', RIOT_PLATING_COST_PARTS);
    spendCoins(wallet, RIOT_PLATING_COST_COINS);
    playerHealth.maxHp += RIOT_PLATING_BONUS_HP;
    playerHealth.hp = Math.min(playerHealth.maxHp, playerHealth.hp + RIOT_PLATING_BONUS_HP);
    progress.platingCrafted = true;
    return { changed: true, toast: `🛡️ RIOT PLATING CRAFTED — +${RIOT_PLATING_BONUS_HP} MAX HP` };
  }

  // Salvage Exchange — repeatable forever, the bench's permanent "do
  // something useful with extra parts" utility once both HP tiers are done.
  if (wallet.materials.part < SALVAGE_EXCHANGE_COST_PARTS) {
    return { changed: false, toast: `♻️ SALVAGE EXCHANGE — need ${SALVAGE_EXCHANGE_COST_PARTS} parts` };
  }
  spendMaterial(wallet, 'part', SALVAGE_EXCHANGE_COST_PARTS);
  grantCoins(wallet, SALVAGE_EXCHANGE_GAIN_COINS);
  grantMaterial(wallet, 'scrap', SALVAGE_EXCHANGE_GAIN_SCRAP);
  return { changed: true, toast: `♻️ SALVAGED ${SALVAGE_EXCHANGE_COST_PARTS} PARTS — +${SALVAGE_EXCHANGE_GAIN_COINS} COINS, +${SALVAGE_EXCHANGE_GAIN_SCRAP} SCRAP` };
}
